import type { LazyQueryHookOptions } from '@apollo/react-hooks'
import type { SearchContactsQuery } from '@paintscout/api'
import { useSearchContactsLazyQuery } from '@paintscout/api'
import { useState } from 'react'

/**
 * A helper hook to simplify managing contact search and pagination
 */
export function useContactSearch(
  options: { query?: string; fetchPolicy?: string; limit?: number; prevBookmark?: string; prevSort?: string[] } & Omit<
    LazyQueryHookOptions,
    'query'
  > = {}
) {
  const { limit, query, fetchPolicy = 'cache-first', prevBookmark, prevSort, ...queryOptions } = options
  const [bookmark, setBookmark] = useState<string>(prevBookmark ?? undefined)
  const [lastQuery, setLastQuery] = useState(query)
  const [sort, setSort] = useState<string[]>(prevSort ?? [])

  const [searchContacts, queryResults] = useSearchContactsLazyQuery({
    fetchPolicy,
    ...(queryOptions as any), // ? this ts error is stupid
    onCompleted: (data) => {
      if (data?.searchContacts) {
        setBookmark(data.searchContacts.bookmark)
      }
      if (queryOptions.onCompleted) {
        queryOptions.onCompleted(data)
      }
    }
  })

  const search = ({ ...args }: { query: string; limit?: number; sort?: string[] }) => {
    setLastQuery(args.query)

    if (args.sort) {
      setSort(args.sort)
    }

    searchContacts({
      variables: {
        limit,
        ...args,
        sort: args.sort || sort
      }
    })
  }

  const fetchMore = () => {
    return queryResults.fetchMore({
      variables: { query: lastQuery, sort, limit, bookmark },
      updateQuery: (previousResult: SearchContactsQuery, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.searchContacts) {
          return previousResult
        }

        return {
          ...previousResult,
          ...fetchMoreResult,
          searchContacts: {
            ...previousResult.searchContacts,
            ...fetchMoreResult.searchContacts,
            bookmark: fetchMoreResult.searchContacts.bookmark,
            contacts: [...previousResult.searchContacts.contacts, ...fetchMoreResult.searchContacts.contacts]
          }
        }
      }
    })
  }

  const newQueryResults = { ...queryResults, fetchMore }

  return [search, newQueryResults] as [typeof search, typeof newQueryResults]
}
